/**
 * 贷款事件。与 LOS `web/los/src/statsig/events/loan.ts` 同名对齐：
 * STATSIG.LOAN.UPDATE_1003 → LOAN_1003_UPDATE
 *
 * 借款端额外：
 * - INSERT：createnewapplication / signupfrompreapp 建贷
 *
 * LOS 当前 UPDATE_1003 主要接在 profile PATCH；借款端 V2 向导各步 PATCH 成功都打，
 * 并用 metadata.section 区分 yourinfo|residence|…（LOS 单页 1003 暂无 section）。
 */
export enum LoanEvent {
    /**
     * 切左侧菜单（与 LOS 同名）。
     * value 与 metadata.tab 相同，如 overview、1003、rate_recommendation
     */
    TAB = 'LOAN_TAB',
    /**
     * 建贷成功。
     * value / metadata.loan_id = 新 LoanID（无则 value=source）
     * 属性 source=preapp|my_loans_v2|…
     */
    INSERT = 'LOAN_INSERT',
    /**
     * 1003 落库（与 LOS 同名）。
     * value 与 metadata.party_id 相同
     * 借款端另加：section、loan_id
     *
     * 跨端统计：LOS 主要只打 profile；借款端各步都打。
     * 与 LOS 对齐的可比指标请过滤 metadata.section=yourinfo（≈ profile）。
     */
    UPDATE_1003 = 'LOAN_1003_UPDATE',
    /**
     * 添加 coborrower 成功（与 LOS 同名）。
     * value 与 metadata.loan_id 相同
     * 属性 type=insert；borrower_count
     */
    ADD_CO_BORROWER_BUTTON = 'LOAN_ADD_CO_BORROWER_BUTTON',
    /**
     * 删除 coborrower（与 LOS 同名）。
     * value 与 metadata.type 相同：delete
     * 属性 party_id、borrower_count
     */
    DELETE_CO_BORROWER_BUTTON = 'LOAN_DELETE_CO_BORROWER_BUTTON',
    /**
     * 切换借款人 tab（与 LOS 同名）。
     * value 与 metadata.party_id 相同
     * 属性 role=borrower|coborrower
     */
    BORROWER_SWITCH = 'LOAN_BORROWER_SWITCH',
    /**
     * Authorize & submit / 提交申请成功。
     * value 与 metadata.loan_id 相同
     * 属性 party_id；type=authorize_and_submit|submit
     */
    SUBMIT = 'LOAN_SUBMIT',
    /**
     * 选定利率并保存（对齐 LOS RATE_SELECTION）。
     * value 与 metadata.type 相同：done
     */
    RATE_SELECTION = 'LOAN_RATE_SELECTION',
    /**
     * 借款人上传文档（对齐 LOS DOCUMENT_UPLOAD：仅成功后打）。
     * value=文件数；属性 mode=single|reupload|multiple；file_count
     */
    DOCUMENT_UPLOAD = 'LOAN_DOCUMENT_UPLOAD',
    /**
     * Soft credit pull 结果。
     * value 与 metadata.result 相同：success|fail
     * 属性 type=soft；party_id；可选 has_score
     */
    CREDIT_PULL = 'LOAN_CREDIT_PULL',
    /**
     * Pre-qual letter：生成 / 下载 / 分享。
     * value 与 metadata.type 相同：generate|download|share
     */
    PREQUAL_LETTER = 'LOAN_PREQUAL_LETTER',
}

/**
 * 借款端 interview section → LOAN_TAB value（对齐 LOS LOAN_TAB_VALUE）。
 * 1003 向导分步（yourinfo / residence / …）统一记为 1003。
 */
export const LOAN_TAB_VALUE: Record<string, string> = {
    overview: 'overview',
    rates: 'rate_recommendation',
    prequalletter: 'pre_qual_letter',
    documents: 'documents',
}

/** 顶层菜单 tab：overview / 1003 / rate_recommendation / … */
export function loanTabValue(section: string): string {
    if (!section) return '1003'
    return LOAN_TAB_VALUE[section] || '1003'
}

/** LOAN_1003_UPDATE 无 party_id 时的 value 回退 */
export const LOAN_UPDATE_ANON = 'user_not_logged_in'

/**
 * LOAN_1003_UPDATE 无 party（如纯 property PATCH）时的 value：登录邮箱，否则 user_not_logged_in。
 */
export function getLoanUpdateValue(): string {
    if (typeof window === 'undefined') return LOAN_UPDATE_ANON
    if (!sessionStorage.getItem('ZeitroA')) return LOAN_UPDATE_ANON

    try {
        const token = sessionStorage.getItem('ZeitroA')
        const part = token && token.split('.')[1]
        if (part) {
            let b64 = part.replace(/-/g, '+').replace(/_/g, '/')
            const pad = b64.length % 4
            if (pad) b64 += '===='.slice(0, 4 - pad)
            const email = (JSON.parse(atob(b64)).email || '').toLowerCase().trim()
            if (email) return email
        }
    } catch {
        // fall through
    }

    try {
        const raw = sessionStorage.getItem('statsig_user_info')
        if (raw) {
            const user = JSON.parse(raw)
            const email = (
                user?.email ||
                user?.custom?.user_account ||
                user?.custom?.email ||
                ''
            )
                .toLowerCase()
                .trim()
            if (email) return email
        }
    } catch {
        // fall through
    }

    return LOAN_UPDATE_ANON
}
